import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Modal, Button, Card, CardHeader, CardBody } from 'reactstrap';
import { ThemeProps, RTLProps } from '@/shared/prop-types/ReducerProps';
import { Form, Field } from 'react-final-form';
import { getFundingApproval, approveFunding } from '@/service/fundingApprovals';
import { cancelDepositHold } from '@/service/program/invoice';
import { useDispatch, flashSuccess, flashError } from '@/shared/components/flash';
import { queueStatePresentation, effectiveQueueState, canApprove, HOLD_STATUS_LABELS, HOLD_SOURCE_LABELS } from './queueState';
import { formatDateTime, StateBadge } from './columns';
import InvoiceDetailsLink from './InvoiceDetailsLink';

const formatAmount = (value) => `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Invoice dates are calendar dates; format the Y-M-D part without a timezone shift.
const formatDate = (value) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString() : String(value || '');
};

// Money still captured: on hold, or failed to release (both stay in the Pending view).
const CANCELLABLE_STATUSES = ['held', 'failed'];

const Detail = ({ label, children }) => (
    <>
        <div className="review-funding__row">
            <span className="review-funding__label">{label}</span>
            <span className="review-funding__value">{children || '—'}</span>
        </div>
    </>
);

const Section = ({ title, children }) => (
    <section className="review-funding__section">
        <h5 className="review-funding__heading">{title}</h5>
        <div>{children}</div>
    </section>
);

export const ReviewFundingModal = ({ holdId, isOpen, toggle, onResult, theme, rtl }) => {
    const dispatch = useDispatch();
    const [hold, setHold] = useState(null);
    const [loadError, setLoadError] = useState(null);

    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    // Bumped on every open/close, so a request can tell whether the review it started is still the one on screen.
    const session = useRef(0);
    const isCurrent = (started) => mounted.current && session.current === started;

    useEffect(() => {
        session.current += 1;
        if (!isOpen || !holdId) return undefined;
        let active = true;
        setHold(null);
        setLoadError(null);
        getFundingApproval(holdId).then((res) => {
            if (!active) return;
            if (res?.errors) setLoadError(res.errors);
            else setHold(res);
        });
        return () => { active = false; };
    }, [isOpen, holdId]);

    // Re-read the hold after a failed action so the modal shows its current state.
    const reloadDetail = () => {
        const started = session.current;
        return getFundingApproval(holdId).then((res) => {
            if (!isCurrent(started)) return;
            if (res?.errors) setLoadError(res.errors);
            else setHold(res);
        });
    };

    const onSubmit = async (values) => {
        const started = session.current;
        const res = await approveFunding(hold.organization_id, hold.program_id, hold.id, values.notes);
        let close = true;
        if (res?.errors) {
            flashError(dispatch, res.errors);
            close = false;
        } else if (res?.queue_state === 'release_failed') {
            flashError(dispatch, 'Approved, but the release failed — see the invoice to retry.');
        } else if (res?.status === 'released') {
            flashSuccess(dispatch, 'Funding approved and released');
        } else if (res?.queue_state === 'approved_releasing') {
            flashSuccess(dispatch, 'Funding approved — release in progress');
        } else {
            flashSuccess(dispatch, `Approved — funds release at ${formatDateTime(res?.release_at)}`);
        }
        onResult && onResult();
        // Closed, reopened or switched to another hold while the request ran: leave the review now on screen alone.
        if (!isCurrent(started)) return;
        if (close) toggle();
        else await reloadDetail();
    };

    // A deleted invoice or program can't be approved or retried, so cancelling (and refunding) is the way out.
    // Cancel is authorised through the invoice's own program, which may differ from the hold's.
    const onCancelHold = async (values) => {
        const started = session.current;
        const res = await cancelDepositHold(
            hold.organization_id,
            hold.invoice?.program_id ?? hold.program_id,
            hold.invoice?.id ?? hold.invoice_id,
            hold.id,
            values.cancel_notes.trim(),
        );
        if (res?.errors) {
            flashError(dispatch, res.errors);
            if (isCurrent(started)) await reloadDetail();
            return;
        }
        flashSuccess(dispatch, res?.success || 'The held payment has been cancelled.');
        onResult && onResult();
        if (isCurrent(started)) toggle();
    };

    const onInvoiceClosed = () => {
        reloadDetail();
        onResult && onResult();
    };

    const renderBody = () => {
        if (loadError) return <p>{loadError}</p>;
        if (!hold) return <p>Loading...</p>;
        const p = queueStatePresentation(hold);
        const expired = effectiveQueueState(hold) === 'awaiting_approval_hold_expired';
        const holds = (hold.invoice_holds || []).filter((h) => h.id !== hold.id);
        const deletedRecord = hold.invoice_deleted ? 'invoice' : (hold.program_deleted ? 'program' : null);
        return (
            <>
                <Section title="Invoice">
                    <Detail label="Program">{hold.program_name}{hold.program_deleted ? ' (deleted)' : ''}</Detail>
                    <Detail label="Invoice">
                        {hold.invoice_number && (
                            <InvoiceDetailsLink
                                organizationId={hold.organization_id}
                                programId={hold.invoice?.program_id ?? hold.program_id}
                                invoiceId={hold.invoice?.id ?? hold.invoice_id}
                                deleted={Boolean(hold.invoice_deleted || hold.program_deleted)}
                                onClosed={onInvoiceClosed}
                            >
                                {hold.invoice_number}
                            </InvoiceDetailsLink>
                        )}
                        {hold.invoice?.invoice_type ? ` (${hold.invoice.invoice_type})` : ''}
                    </Detail>
                    {hold.invoice?.date_begin && <Detail label="Invoice period start">{formatDate(hold.invoice.date_begin)}</Detail>}
                    {hold.invoice?.date_end && <Detail label="Invoice period end">{formatDate(hold.invoice.date_end)}</Detail>}
                    {hold.invoice?.date_due && <Detail label="Invoice due date">{formatDate(hold.invoice.date_due)}</Detail>}
                </Section>
                <Section title="Payment">
                    <Detail label="Payment amount">{formatAmount(hold.amount)}</Detail>
                    <Detail label="Funding method">{hold.funding_method}</Detail>
                    <Detail label="Gateway transaction ID">{hold.gateway_transaction_id}</Detail>
                    <Detail label="Payment notes">{hold.payment_notes}</Detail>
                    <Detail label="Applied by">{hold.applied_by}</Detail>
                </Section>
                <Section title="Hold & approval">
                    <Detail label="Hold start">{formatDateTime(hold.held_at)}</Detail>
                    <Detail label="Hold expires">{formatDateTime(hold.release_at)}</Detail>
                    <Detail label="Hold status"><StateBadge label={p.holdLabel} color={p.holdColor} outline={p.holdOutline} /></Detail>
                    <Detail label="Approval status"><StateBadge label={p.approvalLabel} color={p.approvalColor} outline={p.approvalOutline} /></Detail>
                    {hold.approved_at && (
                        <>
                            <Detail label="Approved by">{hold.approved_by}</Detail>
                            <Detail label="Approved at">{formatDateTime(hold.approved_at)}</Detail>
                            <Detail label="Approval notes">{hold.approval_notes}</Detail>
                        </>
                    )}
                </Section>
                {holds.length > 0 && (
                    <>
                        <h5 className="review-funding__heading">Other holds on this invoice</h5>
                        <table className="table table-sm">
                            <thead>
                                <tr><th>Amount</th><th>Status</th><th>Source</th><th>Hold start</th><th>Hold expires</th></tr>
                            </thead>
                            <tbody>
                                {holds.map((h) => (
                                    <tr key={h.id}>
                                        <td>{formatAmount(h.amount)}</td>
                                        <td>{HOLD_STATUS_LABELS[h.status] || h.status}</td>
                                        <td>{HOLD_SOURCE_LABELS[h.source] || h.source}</td>
                                        <td>{formatDateTime(h.held_at)}</td>
                                        <td>{formatDateTime(h.release_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
                {deletedRecord && CANCELLABLE_STATUSES.includes(hold.status) && (
                    <Form
                        onSubmit={onCancelHold}
                        validate={(values) => (values.cancel_notes?.trim() ? {} : { cancel_notes: 'A reason is required.' })}
                    >
                        {({ handleSubmit, submitting }) => (
                            <form className="form form--horizontal" onSubmit={handleSubmit}>
                                <p className="text-danger small review-funding__helper">
                                    This {deletedRecord} has been deleted, so this payment can&apos;t be approved or released. Cancel the held payment instead.
                                </p>
                                <div className="form__form-group">
                                    <label className="form__form-group-label" htmlFor="funding-cancel-notes">Reason for cancelling</label>
                                    <div className="form__form-group-field">
                                        <Field name="cancel_notes">
                                            {({ input, meta }) => (
                                                <div className="w-100">
                                                    <textarea {...input} id="funding-cancel-notes" className="form-control" rows={3} maxLength={1000} />
                                                    {meta.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
                                                </div>
                                            )}
                                        </Field>
                                    </div>
                                </div>
                                <div className="d-flex justify-content-end align-items-center">
                                    <Button className="btn-sm mr-2" color="outline-primary" type="button" onClick={toggle}>Close</Button>
                                    <Button className="btn-sm" color="danger" type="submit" disabled={submitting}>Cancel Held Payment</Button>
                                </div>
                            </form>
                        )}
                    </Form>
                )}
                {canApprove(hold) && !deletedRecord && (
                    <Form onSubmit={onSubmit}>
                        {({ handleSubmit, submitting }) => (
                            <form className="form form--horizontal" onSubmit={handleSubmit}>
                                <div className="form__form-group">
                                    <label className="form__form-group-label" htmlFor="funding-approval-notes">Notes (optional)</label>
                                    <div className="form__form-group-field">
                                        <Field name="notes" component="textarea" id="funding-approval-notes" className="form-control" rows={3} maxLength={1000} />
                                    </div>
                                </div>
                                <p className="text-muted small review-funding__helper">
                                    {expired
                                        ? 'Funds will become available immediately.'
                                        : `Approval will be recorded; funds stay held until ${formatDateTime(hold.release_at)}.`}
                                </p>
                                <div className="d-flex justify-content-end align-items-center">
                                    <Button className="btn-sm mr-2" color="outline-primary" type="button" onClick={toggle}>Cancel</Button>
                                    <Button className="btn-sm" color="primary" type="submit" disabled={submitting}>Approve Funding</Button>
                                </div>
                            </form>
                        )}
                    </Form>
                )}
            </>
        );
    };

    return (
        <Modal className={`modal-action modal-lg review-funding ${theme?.className || ''} ${rtl?.direction || 'ltr'}-support`} isOpen={isOpen} toggle={toggle}>
            <Card className="w-100">
                <CardHeader tag="h3">
                    Review Funding
                    <Button className="btn float-end" close onClick={toggle} />
                </CardHeader>
                <CardBody>{renderBody()}</CardBody>
            </Card>
        </Modal>
    );
};

ReviewFundingModal.propTypes = {
    theme: ThemeProps,
    rtl: RTLProps,
};

export default connect((state) => ({
    theme: state.theme,
    rtl: state.rtl,
}))(ReviewFundingModal);
